<template>
  <div class="sheet__header" :style="{ backgroundColor: props.color }">
    <ButtonIconContextual
      class="sheet__close"
      data-testid="sheet__close"
      icon="EF0030"
      size="small"
      label=""
      applied-on="overImage"
      @click="emit('close')"
    />
    <BaseText
      size="p5"
      :color="NebraskaColors.elementOverImage"
      :class="{
        sheet__title: true,
        'sheet__title--show': props.showHeaderText
      }"
    >
      {{ hero?.bar }}
    </BaseText>
    <transition name="slide-fade">
      <div v-if="stopInvestmentBarOpen" class="sheet__regulatory-header">
        <NavBar class="sheet__navbar">
          <template #left>
            <BaseIcon
              :colors="NebraskaColors.elementOverImage"
              :icon="props.hero?.warnings?.icon"
              width="24px"
              height="24px"
            />
          </template>
          <template #center>
            <span>
              <template
                v-for="(content, index) in props.hero?.warnings?.message"
              >
                <span v-if="content.type === 'span'" :key="`span-${index}`">{{
                  content.text
                }}</span>
                <template v-else-if="content.type === 'link'">
                  <a
                    v-if="props.hero?.warnings?.type === 'regulatory_update'"
                    :key="`regulatory-${index}`"
                    href="#"
                    @click.prevent="handleRegulatoryLinkClick"
                  >
                    {{ content.text.trim() }}</a
                  >
                  <a
                    v-else
                    :key="`link-${index}`"
                    :href="content.link"
                    target="_blank"
                    rel="noopener noreferrer"
                    >{{ content.text.trim() }}</a
                  >
                </template>
              </template>
            </span>
          </template>
          <template #right>
            <ButtonIconContextual
              class="sheet__regulatory-close"
              data-testid="sheet__close"
              icon="EF0030"
              size="small"
              label=""
              applied-on="overImage"
              @click="emit('close-stop-investment-bar')"
            />
          </template>
        </NavBar>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { IProductHero } from '@/modules/catalog/services/products';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import {
  BaseIcon,
  BaseText,
  ButtonIconContextual,
  NavBar
} from '@warrenbrasil/nebraska-web';
import { onBeforeUnmount, PropType, ref, watch } from 'vue';
import { useRouter } from 'vue-router/composables';

const router = useRouter();

const props = defineProps({
  showHeaderText: Boolean,
  color: {
    type: String,
    default: ''
  },
  hero: {
    type: Object as PropType<IProductHero>,
    default: null
  },
  stopInvestmentBarOpen: Boolean
});

const emit = defineEmits(['close', 'close-stop-investment-bar']);
const timeoutId = ref<ReturnType<typeof setTimeout> | null>(null);

const handleRegulatoryLinkClick = () => {
  router.push({ name: 'regulatoryUpdateAnnouncement' });
};

watch(
  () => props.stopInvestmentBarOpen,
  newValue => {
    if (timeoutId.value) {
      clearTimeout(timeoutId.value);
      timeoutId.value = null;
    }

    if (newValue) {
      timeoutId.value = setTimeout(() => {
        emit('close-stop-investment-bar');
      }, 6000);
    }
  }
);

onBeforeUnmount(() => {
  if (timeoutId.value) {
    clearTimeout(timeoutId.value);
  }
});
</script>

<style lang="less" scoped>
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.2s ease-in-out;
}

.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateY(-100%);
}

.sheet {
  &__header {
    width: 100%;
    position: sticky;
    height: 64px;
    top: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1;
  }

  &__navbar {
    width: 100%;
    background: @static-black;
    border-bottom: none;
  }

  &__regulatory-header {
    width: 100%;
    position: absolute;
    height: 64px;
    top: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background: @background-primary;
    z-index: 2;

    span,
    a {
      color: @element-over-image;
    }

    a {
      text-decoration: underline;
      &:hover {
        cursor: pointer;
      }
    }
  }

  &__regulatory-close {
    position: relative;
    z-index: 2;

    &:hover {
      cursor: pointer;
    }
  }

  &__close {
    position: absolute;
    top: @size-spacing-x300;
    right: @size-spacing-x400;
    z-index: 1;

    @media screen and (min-width: 1024px) {
      top: @size-spacing-x450;
      left: @size-spacing-x450;
      right: none;
    }

    &:hover {
      cursor: pointer;
    }
  }

  &__title {
    opacity: 0;
    transition: opacity linear 0.3s;

    &--show {
      opacity: 1;
    }
  }
}
</style>
